const childrenLoaded = require('@alexspirgel/children-loaded');

class ComponentHeroLanding extends HTMLDivElement {

  static get mediaQuery() {
		return {
			medium: window.matchMedia('(max-width: 48rem)'), // 768px
			large: window.matchMedia('(max-width: 80rem)'), // 1280px
		};
	}

	constructor() {
		super();
	}

	async connectedCallback() {
		await childrenLoaded(this);
		this.initialize();
	}

	initialize() {

		if (this.initialized) {
			return;
		}

    this.mediaQueryLargeListener(this.constructor.mediaQuery.large);
    this.constructor.mediaQuery.large.addListener(this.mediaQueryLargeListener);
    this.mediaQueryMediumListener(this.constructor.mediaQuery.medium);
    this.constructor.mediaQuery.medium.addListener(this.mediaQueryMediumListener);

		if (this.videoElement && this.videoPlayPauseElement) {
			this.videoPlayPauseElement.addEventListener('click', this.toggleVideoPlayback);
		}
    
    this.updateContentInnerHeightCSSVariable();
    this.updateAccentTextSize();
    this.windowResizeObserver = new ResizeObserver(() => {
      this.updateContentInnerHeightCSSVariable();
      this.updateAccentTextSize();
    });
    this.windowResizeObserver.observe(document.body);

		this.initialized = true;

	}

  mediaQueryLargeListener = (mediaQuery) => {
    if (this.matchesMediaQueryLarge !== mediaQuery.matches) {
      this.matchesMediaQueryLarge = mediaQuery.matches;
      this.mediaQueryLargeChange();
    }
	}

  mediaQueryLargeChange() {
    const contentElement = this.contentElement;
    if (this.matchesMediaQueryLarge) {
      const belowBackgroundAreaElement = this.belowBackgroundAreaElement;
      if (contentElement && belowBackgroundAreaElement) {
        belowBackgroundAreaElement.prepend(contentElement);
      }
    }
    else {
      const backgroundElement = this.backgroundElement;
      if (contentElement && backgroundElement) {
        backgroundElement.append(contentElement);
      }
    }
  }

  mediaQueryMediumListener = (mediaQuery) => {
    if (this.matchesMediaQueryMedium !== mediaQuery.matches) {
      this.matchesMediaQueryMedium = mediaQuery.matches;
      this.mediaQueryMediumChange();
    }
	}

  mediaQueryMediumChange() {
    const accentTextElement = this.accentTextElement;
    if (this.matchesMediaQueryMedium) {
      const belowBackgroundAreaElement = this.belowBackgroundAreaElement;
      const introElement = this.introElement;
      if (accentTextElement && belowBackgroundAreaElement && introElement) {
        belowBackgroundAreaElement.insertBefore(accentTextElement, introElement);
      }
    }
    else {
      if (accentTextElement) {
        this.append(accentTextElement);
      }
    }
  }

  get backgroundElement() {
    return this.querySelector('.component-hero-landing__background');
  }

  get belowBackgroundAreaElement() {
    return this.querySelector('.component-hero-landing__below-background-area');
  }

  get contentElement() {
    return this.querySelector('.component-hero-landing__content');
  }

  get introElement() {
    return this.querySelector('.component-hero-landing__intro');
  }

	get videoElement() {
		return this.querySelector('.component-hero-landing__video');
	}

	get videoPlayPauseElement() {
		return this.querySelector('.component-hero-landing__play-pause');
	}

	get isVideoPlaying() {
    return (this.videoElement
    && this.videoElement.currentTime > 0
    && this.videoElement.paused == false
    && this.videoElement.ended == false);
  }

	pauseVideo() {
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoPlayPauseElement.setAttribute('data-icon', 'play');
      this.videoPlayPauseElement.setAttribute('aria-label', 'play video');
    }
  }

  playVideo() {
    if (this.videoElement) {
      this.videoElement.play();
      this.videoPlayPauseElement.setAttribute('data-icon', 'pause');
      this.videoPlayPauseElement.setAttribute('aria-label', 'pause video');
    }
  }

	toggleVideoPlayback = () => {
    if (this.videoElement) {
      if (this.isVideoPlaying) {
        this.pauseVideo();
      }
      else {
        this.playVideo();
      }
    }
  }

  get contentInnerElement() {
    return this.querySelector('.component-hero-landing__content-inner');
  }

  updateContentInnerHeightCSSVariable() {
		if (this.contentInnerElement && typeof this.contentInnerElement.offsetHeight === 'number') {
			const height = this.contentInnerElement.offsetHeight.toString() + 'px';
      this.style.setProperty('--content-inner-height', height);
		}
    else {
      this.style.removeProperty('--content-inner-height');
    }
  }

  get accentTextElement() {
    return this.querySelector('.component-hero-landing__accent-text');
  }

  get accentTextInnerElement() {
    return this.querySelector('.component-hero-landing__accent-text-inner:not(.sizer)');
  }

  get accentTextInnerSizerElement() {
    return this.querySelector('.component-hero-landing__accent-text-inner.sizer');
  }

  updateAccentTextSize() {
    const accentTextInnerElement = this.accentTextInnerElement;
    if (!accentTextInnerElement) {
      return;
    }
    const accentTextInnerTextElement = this.accentTextInnerElement.querySelector('.text');
    const accentTextInnerSizerElement = this.accentTextInnerSizerElement;
    const belowBackgroundAreaElement = this.belowBackgroundAreaElement;

    if (accentTextInnerElement && accentTextInnerTextElement && accentTextInnerSizerElement && belowBackgroundAreaElement) {
      const heroHeight = this.offsetHeight;
      const heroPaddingBottomPropertyValue = window.getComputedStyle(belowBackgroundAreaElement).getPropertyValue('padding-bottom');
      const heroPaddingHeight = (parseInt(heroPaddingBottomPropertyValue) * 2);
      let usableHeroHeight = heroHeight - heroPaddingHeight;
      if (this.matchesMediaQueryMedium) {
        const introElement = this.introElement;
        if (introElement) {
          const introHeight = introElement.offsetHeight;
          const introMarginTopPropertyValue = window.getComputedStyle(introElement).getPropertyValue('margin-top');
          const introMarginHeight = parseInt(introMarginTopPropertyValue);
          usableHeroHeight = usableHeroHeight - introHeight - introMarginHeight;
        }
      }

      const fontSize = parseInt(window.getComputedStyle(accentTextInnerElement).getPropertyValue('font-size'));
      const sizerFontSize = parseInt(window.getComputedStyle(accentTextInnerSizerElement).getPropertyValue('font-size'));
      const accentHeight = accentTextInnerTextElement.offsetWidth; // Use offsetWidth because of the rotation.
      
      const fontDifference = usableHeroHeight / accentHeight
      const updatedFontSize = Math.min((fontSize * fontDifference), sizerFontSize);
      accentTextInnerElement.style.fontSize = `${updatedFontSize}px`;
      accentTextInnerElement.classList.add('font-resized-js');
    }
  }

}

customElements.define('component-hero-landing', ComponentHeroLanding, {extends: 'div'});